import { useEffect } from 'react';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs';
import {
  ROUTE_PERSONS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK
} from 'routes/RouteList';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';

import {
  ActionButton,
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper
} from 'components/panel';

import Loader from 'components/loader/Loader';
import { TabPanelBody, Tabs } from 'components/tabs';
import { TabList, TabPanel } from 'react-tabs';
import { getLocalDateFormat } from 'utils/date';
import useTabIndex from 'hooks/utils/useTabIndex';
import useProgram from 'hooks/queries/useProgram';
import { useStore } from 'stores/Store';

import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import ContextMenu from 'components/menu/ContextMenu';
import PersonProgramExercisesTab from 'containers/pages/persons/PersonProgramExercisesTab';
import PersonProgramFeedbackTab from 'containers/pages/persons/PersonProgramFeedbackTab';
import TabLink from 'components/tabs/TabLink';

const PersonProgram = () => {
  const { fetchProgram, loading } = useProgram();
  const { entityId, groupId, personId, programId } = useParams();
  const program = useStore(state => state.program);

  const [tabIndex, setTabIndex] = useTabIndex([
    ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
    ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK
  ]);
  const active = useRouteMatch([
    ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK,
    ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM
  ]);

  useEffect(() => {
    if (programId) {
      fetchProgram({ variables: { id: programId } });
    }
  }, [programId]);

  if (loading || !program?.id) {
    return (
      <Panel active={active.isExact}>
        <Loader />
      </Panel>
    );
  }

  return (
    <Panel active={active.isExact}>
      <PanelHeader>
        <Breadcrumbs>
          <Breadcrumb
            to={generatePath(ROUTE_PERSONS_ENTITY, {
              entityId,
              groupId
            })}
          >
            <FormattedMessage {...messages.breadcrumbSporters} />
          </Breadcrumb>
          <Breadcrumb
            to={generatePath(ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS, {
              entityId,
              groupId,
              personId
            })}
          >
            <FormattedMessage {...messages.breadcrumbPrograms} />
          </Breadcrumb>
        </Breadcrumbs>
        <PanelTitleWrapper>
          <PanelTitle>{program.title}</PanelTitle>
          <PanelSubtitle>
            <FormattedMessage
              {...messages.exercisesProgramSubTitle}
              values={{
                startdate: getLocalDateFormat(program.startdate),
                duration: program.duration
              }}
            />
          </PanelSubtitle>
        </PanelTitleWrapper>
        <PanelTitleButtonsGroup>
          <ActionButton />
          <ContextMenuWrapper trigger={<PanelTitleButton />}>
            <ContextMenu />
          </ContextMenuWrapper>
        </PanelTitleButtonsGroup>
      </PanelHeader>
      <PanelBody>
        {loading && <Loader />}

        <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
          <TabList>
            <TabLink
              to={generatePath(
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
                {
                  entityId,
                  groupId,
                  personId,
                  programId
                }
              )}
            >
              <FormattedMessage {...messages.tabExercises} />
            </TabLink>
            <TabLink
              to={generatePath(
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK,
                {
                  entityId,
                  groupId,
                  personId,
                  programId
                }
              )}
            >
              <FormattedMessage {...messages.tabFeedback} />
            </TabLink>
          </TabList>
          <TabPanel>
            <PersonProgramExercisesTab />
          </TabPanel>
          <TabPanel>
            <TabPanelBody withScroller>
              <PersonProgramFeedbackTab />
            </TabPanelBody>
          </TabPanel>
        </Tabs>
      </PanelBody>
    </Panel>
  );
};

export default PersonProgram;
